const HEX_PATTERN = /^#[0-9A-F]{6}$/;
const CUSTOM_COLOUR_VALUES = new Set(['Custom Colour', 'Custom Color']);
const BACKGROUND_OPTION_NAMES = new Set(['Background Colour', 'Background Color']);
const customColourByProduct = new Map();

export function storedQuoteColour(productId, productForm, fallback = '#FFFFFF') {
  return customColourByProduct.get(productId) || productForm?.dataset.pnpQuoteColourHex || fallback;
}

export function persistQuoteColour(productId, productForm, value) {
  if (productId) customColourByProduct.set(productId, value);
  if (productForm) productForm.dataset.pnpQuoteColourHex = value;
}

class QuoteColourCustomizer extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const section = this.closest('.shopify-section');
    const { signal } = this.#abortController;

    this.productForm = document.getElementById(this.dataset.productFormId);

    section?.addEventListener('change', this.#handleChange, { signal });
    section?.addEventListener('input', this.#handleChange, { signal });
    section?.addEventListener('shopify:product:select', this.#handleProductSelect, { signal });
    this.#syncFromVariant();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) && !(event.target instanceof HTMLSelectElement)) return;

    if (event.target.matches('.quote-colour-customizer__picker')) {
      this.#setHex(event.target.value);
      return;
    }

    if (event.target.matches('.quote-colour-customizer__hex')) {
      this.#setHex(event.target.value, { normalizeOnlyWhenComplete: true });
      return;
    }

    if (BACKGROUND_OPTION_NAMES.has(event.target.dataset.optionName ?? '')) this.#syncFromVariant();
  };

  #handleProductSelect = (event) => {
    const eventPromise = event.promise;
    if (eventPromise instanceof Promise) {
      eventPromise
        .then(() => requestAnimationFrame(() => this.#syncFromVariant()))
        .catch(() => {});
      return;
    }

    requestAnimationFrame(() => this.#syncFromVariant());
  };

  #getBackgroundColour() {
    const section = this.closest('.shopify-section');
    const selected = Array.from(section?.querySelectorAll('variant-picker [data-option-name]') ?? []).find(
      (option) => option instanceof HTMLElement && BACKGROUND_OPTION_NAMES.has(option.dataset.optionName ?? '')
        && ((option instanceof HTMLInputElement && option.checked) || (option instanceof HTMLOptionElement && option.selected))
    );

    if (selected instanceof HTMLInputElement || selected instanceof HTMLOptionElement) return selected.value;

    const select = Array.from(section?.querySelectorAll('variant-picker select') ?? []).find((candidate) => {
      if (!(candidate instanceof HTMLSelectElement)) return false;
      const optionName = candidate.name.replace(/^options\[/, '').replace(/\]$/, '');
      return BACKGROUND_OPTION_NAMES.has(optionName);
    });
    if (select instanceof HTMLSelectElement) return select.value;

    return '';
  }

  #syncFromVariant() {
    const fields = this.querySelector('.quote-colour-customizer__fields');
    const inputs = this.querySelectorAll('input');
    const isCustom = CUSTOM_COLOUR_VALUES.has(this.#getBackgroundColour());

    if (fields instanceof HTMLElement) fields.hidden = !isCustom;
    inputs.forEach((input) => {
      input.disabled = !isCustom;
    });

    const hexInput = this.querySelector('.quote-colour-customizer__hex');
    if (hexInput instanceof HTMLInputElement) {
      hexInput.required = isCustom;
      hexInput.setAttribute('aria-required', isCustom ? 'true' : 'false');
    }

    if (isCustom) {
      const fallback = hexInput instanceof HTMLInputElement ? hexInput.value : '#FFFFFF';
      this.#setHex(storedQuoteColour(this.dataset.productId, this.productForm, fallback));
    }
  }

  #setHex(rawValue, { normalizeOnlyWhenComplete = false } = {}) {
    const hexInput = this.querySelector('.quote-colour-customizer__hex');
    const picker = this.querySelector('.quote-colour-customizer__picker');
    const error = this.querySelector('.quote-colour-customizer__error');
    const visibleProperty = this.querySelector('[data-quote-colour-visible-property]');
    const internalProperty = this.querySelector('[data-quote-colour-internal-property]');
    let value = rawValue.trim().toUpperCase();

    if (value && !value.startsWith('#')) value = `#${value}`;
    const isValid = HEX_PATTERN.test(value);

    if (hexInput instanceof HTMLInputElement && (!normalizeOnlyWhenComplete || isValid)) hexInput.value = value;
    if (hexInput instanceof HTMLInputElement) {
      hexInput.setCustomValidity(isValid ? '' : 'Enter a valid colour in the format #AABBCC.');
      hexInput.setAttribute('aria-invalid', isValid ? 'false' : 'true');
    }
    if (error instanceof HTMLElement) error.hidden = isValid;
    if (!isValid) return;

    if (picker instanceof HTMLInputElement) picker.value = value;
    if (visibleProperty instanceof HTMLInputElement) {
      visibleProperty.value = `${this.dataset.customColourPrefix || 'Custom'} (${value})`;
    }
    if (internalProperty instanceof HTMLInputElement) internalProperty.value = value;
    persistQuoteColour(this.dataset.productId, this.productForm, value);
  }
}

if (!customElements.get('quote-colour-customizer')) {
  customElements.define('quote-colour-customizer', QuoteColourCustomizer);
}
